import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { ArrowBackIosNew } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Divider, Drawer, IconButton, Stack, Typography } from "@mui/material";
import { useState } from "react";
import TextButton from "@/components/TextButton";
import { useCampground } from "@/contexts/AppConfigContext";
import { useBookingContext } from "@/contexts/BookingContext";
import { useStepNavigation } from "@/hooks/useStepNavigation";
import { primaryActionSx, theme } from "@/theme/mui-theme";
import { formatCampgroundAddress } from "@/utils/address";
import { formatDateRange, formatGuests, getCampsiteTypeLabel } from "@/utils/booking";
import { formatCurrency, formatPaymentInfo } from "@/utils/pricing";
import BookingSearchDetails from "./BookingSearchDetails";
import PriceBreakdown from "./PriceBreakdown";

interface MobileBookingBottomSheetProps {
  nextButtonText?: string;
  nextLoading?: boolean;
  nextDisabled?: boolean;
  nextAction?: () => Promise<boolean>;
}

export default function MobileBookingBottomSheet({
  nextButtonText,
  nextLoading,
  nextDisabled,
  nextAction,
}: MobileBookingBottomSheetProps) {
  const { state } = useBookingContext();
  const { data: campgroundData } = useCampground();
  const { i18n } = useLingui();
  const { goToNextStep, goToPreviousStep, nextStep, previousStep } = useStepNavigation();
  const [detailsOpen, setDetailsOpen] = useState(false);

  const price = state.price;

  const formatPrice = (value: number) =>
    price ? formatCurrency(value, price.currency, i18n.locale) : null;
  const formatPriceAlt = (value: number) =>
    price?.currency_alt ? formatCurrency(value, price.currency_alt.currency, i18n.locale) : null;

  const handleNext = async () => {
    if (nextAction) {
      if (await nextAction()) {
        goToNextStep();
      }
    } else {
      goToNextStep();
    }
  };

  const campsiteTypeLabel = getCampsiteTypeLabel(
    state.selectedCampsiteType,
    campgroundData.campsite_types,
  );
  const dateRangeLabel = formatDateRange([state.dateFrom, state.dateUntil], i18n.locale);
  const guestsLabel = formatGuests(state.selectedPersons, campgroundData.campground?.persons || []);

  const buttonSx = nextStep === "confirmation" ? primaryActionSx : {};
  const buttonLabel = nextButtonText || t`Next`;

  const paymentInfo = formatPaymentInfo(price?.payment_system || "", i18n);
  const cancellationTerms = price?.cancellation_terms || [];

  return (
    <>
      {/* Collapsed bottom sheet */}
      <Stack
        sx={{
          bgcolor: "background.paper",
          p: 2,
          height: 180,
          boxShadow: "0 -4px 6px rgba(0, 0, 0, 0.1)",
          display: { xs: "block", md: "none" },
        }}
        spacing={2}
      >
        {/* Price + details summary */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {formatPrice(price?.total || 0)}
            {price?.currency_alt && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                ({formatPriceAlt(price.currency_alt.total)})
              </Typography>
            )}
          </Typography>
          <TextButton onClick={() => setDetailsOpen(true)} sx={{ fontWeight: 600 }}>
            <Trans>More details</Trans>
          </TextButton>
        </Stack>

        <Stack>
          <Typography variant="body2" textTransform="capitalize" color="text.secondary">
            {[campsiteTypeLabel, dateRangeLabel].filter(Boolean).join(", ")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {guestsLabel}
          </Typography>
        </Stack>

        {/* Navigation buttons */}
        <Stack direction="row" spacing={2} alignItems="center">
          {previousStep && (
            <IconButton
              onClick={goToPreviousStep}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                width: 60,
                height: 60,
                flexShrink: 0,
              }}
            >
              <ArrowBackIosNew sx={{ color: "text.primary" }} />
            </IconButton>
          )}
          <Button
            fullWidth
            variant="contained"
            size="large"
            loading={nextLoading}
            onClick={handleNext}
            disabled={nextDisabled}
            sx={{ height: 60, ...buttonSx }}
          >
            {buttonLabel}
          </Button>
        </Stack>
      </Stack>

      {/* Expanded details drawer */}
      <Drawer
        anchor="bottom"
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        slotProps={{
          paper: {
            sx: {
              height: "90vh",
              borderRadius: "20px 20px 0 0",
              overflow: "hidden",
            },
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          {/* Drawer header */}
          <Box sx={{ px: 3, pt: 3, pb: 1, flexShrink: 0 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h3">
                <Trans>Booking Details</Trans>
              </Typography>
              <IconButton onClick={() => setDetailsOpen(false)} edge="end">
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>

          {/* Scrollable content */}
          <Box sx={{ flex: 1, overflowY: "auto", px: 3, pb: 2 }}>
            <Stack spacing={2.5}>
              {/* Campground info */}
              <Box>
                <Typography variant="h4" gutterBottom>
                  {campgroundData.campground?.name || ""}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {formatCampgroundAddress(campgroundData.campground, campgroundData.countries)}
                </Typography>
              </Box>

              <Divider />

              {/* Search details with edit link */}
              <BookingSearchDetails />

              <Divider />

              {/* Price Details */}
              <Box>
                <Typography variant="h3" gutterBottom>
                  <Trans>Price Details</Trans>
                </Typography>
                {price && <PriceBreakdown price={price} />}
                <Divider sx={{ my: 2 }} />
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Typography variant="body1" fontWeight={600}>
                    <Trans>Total price</Trans>
                  </Typography>
                  <Stack alignItems="flex-end">
                    <Typography variant="body1" fontWeight={600}>
                      {formatPrice(price?.total || 0)}
                    </Typography>
                    {price?.currency_alt && (
                      <Typography variant="body2" color="text.secondary">
                        ({formatPriceAlt(price.currency_alt.total)})
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              </Box>
              {/* Cancellation Terms */}
              {cancellationTerms.length > 0 && (
                <>
                  <Box>
                    <Typography variant="h4" gutterBottom>
                      <Trans>Cancellation Policy</Trans>
                    </Typography>
                    <Stack spacing={0.5}>
                      {cancellationTerms.map((term) => (
                        <Typography variant="body1" key={term}>
                          {term}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                  <Divider />
                </>
              )}

              {/* Payment Info */}
              {paymentInfo && (
                <>
                  <Box>
                    <Typography variant="h4" gutterBottom>
                      <Trans>Payment Information</Trans>
                    </Typography>
                    <Typography variant="body1">{paymentInfo}</Typography>
                  </Box>
                  <Divider />
                </>
              )}
            </Stack>
          </Box>

          {/* Bottom action button */}
          <Box sx={{ px: 3, pb: 3, pt: 2, flexShrink: 0 }}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              loading={nextLoading}
              onClick={() => {
                setDetailsOpen(false);
                handleNext();
              }}
              disabled={nextDisabled}
              sx={buttonSx}
            >
              {buttonLabel}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
